/**
 * Компонент для добавления новых полей в конфигурацию
 */

import React, { FC, useState } from 'react';
import { Modal, Form, Input, Select, Button, InputNumber, Switch } from 'antd';
import { PlusOutlined } from '@ant-design/icons';

const { Option } = Select;

interface IFieldEditorProps {
  onAddField: (
    fieldName: string,
    fieldType: string,
    defaultValue: any,
    parentPath?: string[]
  ) => void;
  existingFields: string[];
  parentPath?: string[];
  visible?: boolean;
  onClose?: () => void;
}

const FieldEditor: FC<IFieldEditorProps> = ({
  onAddField,
  existingFields,
  parentPath = [],
  visible: controlledVisible,
  onClose
}) => {
  const [internalVisible, setInternalVisible] = useState(false);
  const [form] = Form.useForm();

  const visible = controlledVisible !== undefined ? controlledVisible : internalVisible;

  const handleOpen = () => {
    setInternalVisible(true);
  };

  const handleCancel = () => {
    if (onClose) {
      onClose();
    } else {
      setInternalVisible(false);
    }
    form.resetFields();
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const { fieldName, fieldType, defaultValue } = values;

      // Проверяем уникальность имени поля
      if (existingFields.includes(fieldName)) {
        form.setFields([
          {
            name: 'fieldName',
            errors: ['Поле с таким именем уже существует']
          }
        ]);
        return;
      }

      // Преобразуем значение по умолчанию в соответствии с типом
      let finalValue: any;
      switch (fieldType) {
        case 'boolean':
          finalValue = defaultValue === 'true' || defaultValue === true;
          break;
        case 'number':
          finalValue = Number(defaultValue) || 0;
          break;
        case 'object':
          finalValue = {};
          break;
        default:
          finalValue = defaultValue || '';
      }

      onAddField(fieldName, fieldType, finalValue, parentPath);
      handleCancel();
    } catch (error) {
      // Validation failed
    }
  };

  const renderDefaultValueField = () => {
    const fieldType = form.getFieldValue('fieldType');

    switch (fieldType) {
      case 'boolean':
        return (
          <Form.Item name="defaultValue" label="Значение по умолчанию" initialValue={false}>
            <Switch />
          </Form.Item>
        );
      case 'number':
        return (
          <Form.Item name="defaultValue" label="Значение по умолчанию" initialValue={0}>
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
        );
      case 'object':
        return (
          <Form.Item label="Значение по умолчанию">
            <Input disabled value="{}" />
          </Form.Item>
        );
      default:
        return (
          <Form.Item name="defaultValue" label="Значение по умолчанию" initialValue="">
            <Input />
          </Form.Item>
        );
    }
  };

  const modalTitle =
    parentPath.length > 0
      ? `Добавить поле в ${parentPath[parentPath.length - 1]}`
      : 'Добавить новое поле';

  return (
    <>
      {controlledVisible === undefined && (
        <Button type="dashed" onClick={handleOpen} block icon={<PlusOutlined />}>
          Добавить поле
        </Button>
      )}

      <Modal
        title={modalTitle}
        open={visible}
        onOk={handleOk}
        onCancel={handleCancel}
        okText="Добавить"
        cancelText="Отмена"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="fieldName"
            label="Имя поля"
            rules={[
              { required: true, message: 'Введите имя поля' },
              {
                pattern: /^[a-zA-Z][a-zA-Z0-9_]*$/,
                message: 'Имя должно начинаться с буквы и содержать только буквы, цифры и _'
              }
            ]}
          >
            <Input placeholder="например: newField" />
          </Form.Item>

          <Form.Item
            name="fieldType"
            label="Тип поля"
            rules={[{ required: true, message: 'Выберите тип поля' }]}
            initialValue="string"
          >
            <Select onChange={() => form.setFieldsValue({ defaultValue: undefined })}>
              <Option value="string">Строка</Option>
              <Option value="number">Число</Option>
              <Option value="boolean">Логическое</Option>
              <Option value="object">Объект</Option>
            </Select>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.fieldType !== currentValues.fieldType
            }
          >
            {() => renderDefaultValueField()}
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default FieldEditor;
